import React from 'react';
import cn from 'classnames';
import CornerCell from './components/CornerCell';
import SideColumn from './components/SideColumn';
import TopRow from './components/TopRow';
import { views } from '../../constants/views';
import MonthView from './components/MonthView';
import DayView from './components/DayView';
import WeekView from './components/WeekView';
import AgendaView from './components/AgendaView';
import TasksModal from './components/TasksModal';
import ResourceView from './components/ResourceView';
import { containerIds } from '../../constants/containerIds';
import {
  getGridTemplateColumns,
  getGridTemplateRows,
  getRowCount,
} from './utils';
import { modes } from '../../constants/modes';
import {
  setTasksModalData,
  setTasksModalVisible,
} from '../../store/actions/calendarActions';
import {
  useCalendarDispatch,
  useCalendarState,
} from '../../context/CalendarContext';
import styles from './CalendarGrid.module.scss';

const Grid = ({
  totalTime,
  rootClassName,
  resourceRows,
  resourceColumnTitle,
  resourceColumnWidth = '320px',
  resourceDateColumnMinWidth = '116px',
  resourceDateColumnMaxWidth = '1fr',
  resourceHeaderHeight = '56px',
  resourceRowMinHeight = '96px',
  resourceCompact = false,
  renderResourceRowHeader,
  renderResourceFooter,
  renderResourceTask,
  renderResourceEmptyTask,
  resourceEmptyText,
}) => {
  const dispatch = useCalendarDispatch();
  const { view, mode, showOwner, calendarData } = useCalendarState();

  const rowCount = getRowCount(mode, view, totalTime);
  const isResourceView = Array.isArray(resourceRows);
  const hasResourceFooter =
    isResourceView && typeof renderResourceFooter === 'function';

  const calendarViews = {
    [views.month]: <MonthView />,
    [views.week]: <WeekView rowCount={rowCount} />,
    [views.day]: <DayView rowCount={rowCount} />,
  };

  const handleTasksModalCancel = () => {
    dispatch(setTasksModalVisible(false));
  };

  const afterTasksModalClose = () => {
    dispatch(setTasksModalData(null));
  };

  const heightTask = showOwner && mode === modes.calendar && view === views.day;
  const resourceColumnCount = Math.max(calendarData.length, 1);
  const resourceGridTemplateColumns = [
    resourceColumnWidth,
    `repeat(${resourceColumnCount}, minmax(${resourceDateColumnMinWidth}, ${resourceDateColumnMaxWidth}))`,
  ].join(' ');
  const resourceGridTemplateRows = [
    resourceHeaderHeight,
    `repeat(${Math.max(
      resourceRows?.length || 0,
      1
    )}, minmax(${resourceRowMinHeight}, auto))`,
    hasResourceFooter ? 'auto' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      id={containerIds.calendarGrid}
      className={cn(
        styles.root,
        isResourceView && styles.resourceRoot,
        isResourceView && resourceCompact && styles.resourceCompact,
        rootClassName,
        !isResourceView &&
          mode === modes.calendar &&
          view !== views.day &&
          styles.printCalendar,
        !isResourceView &&
          mode === modes.calendar &&
          view === views.day &&
          styles.longPrint,
        'TESTGRID'
      )}
      style={{
        gridTemplateColumns: isResourceView
          ? resourceGridTemplateColumns
          : getGridTemplateColumns(mode, view),
        gridTemplateRows: isResourceView
          ? resourceGridTemplateRows
          : getGridTemplateRows(mode, view, rowCount, heightTask, calendarData),
      }}
    >
      {isResourceView ? (
        <ResourceView
          resourceRows={resourceRows}
          resourceColumnTitle={resourceColumnTitle}
          renderResourceRowHeader={renderResourceRowHeader}
          renderResourceFooter={renderResourceFooter}
          renderResourceTask={renderResourceTask}
          renderResourceEmptyTask={renderResourceEmptyTask}
          emptyText={resourceEmptyText}
        />
      ) : (
        <>
          {mode === modes.calendar && (
            <>
              <TopRow />
              <CornerCell />
            </>
          )}
          <SideColumn rowCount={rowCount} />
          {mode === modes.calendar ? calendarViews[view] : <AgendaView />}
        </>
      )}
      <TasksModal
        afterClose={afterTasksModalClose}
        onCancel={handleTasksModalCancel}
        taskName={view === views.month ? 'tasks' : 'daylongTasks'}
      />
    </div>
  );
};

export default Grid;
